import { useState, useRef, useCallback } from 'react'

export type VoiceRecorderState = 'idle' | 'recording' | 'transcribing'

const LEVEL_BAR_COUNT = 5
const LEVEL_SHIFT_MS = 60 // each bar shifts every 60ms → 300ms total travel

type UseVoiceRecorderOptions = {
  csrfToken: string
  onTranscription: (text: string) => void
  onError?: (error: string) => void
}

export function useVoiceRecorder({ csrfToken, onTranscription, onError }: UseVoiceRecorderOptions) {
  const [state, setState] = useState<VoiceRecorderState>('idle')
  const [levelHistory, setLevelHistory] = useState<number[]>(() => Array(LEVEL_BAR_COUNT).fill(0))
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animFrameRef = useRef<number>(0)
  const shiftIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const currentLevelRef = useRef(0)

  const stopMediaTracks = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = 0
    }
    if (shiftIntervalRef.current) {
      clearInterval(shiftIntervalRef.current)
      shiftIntervalRef.current = null
    }
    audioContextRef.current?.close()
    audioContextRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    currentLevelRef.current = 0
    setLevelHistory(Array(LEVEL_BAR_COUNT).fill(0))
  }, [])

  const startLevelMonitoring = useCallback((stream: MediaStream) => {
    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 256
    const source = audioContext.createMediaStreamSource(stream)
    source.connect(analyser)
    audioContextRef.current = audioContext

    const dataArray = new Uint8Array(analyser.fftSize)

    // rAF loop: update current level ref (no React state, no re-renders)
    const updateLevel = () => {
      analyser.getByteTimeDomainData(dataArray)
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        const val = (dataArray[i] - 128) / 128
        sum += val * val
      }
      const rms = Math.sqrt(sum / dataArray.length)
      currentLevelRef.current = Math.min(1, rms * 5)
      animFrameRef.current = requestAnimationFrame(updateLevel)
    }
    updateLevel()

    // Shift register: sample current level into history every LEVEL_SHIFT_MS
    shiftIntervalRef.current = setInterval(() => {
      setLevelHistory(prev => [...prev.slice(1), currentLevelRef.current])
    }, LEVEL_SHIFT_MS)
  }, [])

  const transcribe = useCallback(async (blob: Blob) => {
    setState('transcribing')
    try {
      const formData = new FormData()
      formData.append('audio', blob, 'recording.webm')

      const response = await fetch('/transcriptions', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.error || `Transcription failed (${response.status})`)
      }

      const data = await response.json()
      if (data.text?.trim()) {
        onTranscription(data.text.trim())
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Transcription failed')
    } finally {
      setState('idle')
    }
  }, [csrfToken, onTranscription, onError])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      chunksRef.current = []

      startLevelMonitoring(stream)

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : undefined

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        stopMediaTracks()
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
        if (blob.size < 1000) {
          setState('idle')
          onError?.('Recording too short')
          return
        }
        transcribe(blob)
      }

      recorder.start()
      setState('recording')
    } catch (err) {
      stopMediaTracks()
      setState('idle')
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          onError?.('Microphone permission denied')
        } else if (err.name === 'NotFoundError') {
          onError?.('No microphone found')
        } else {
          onError?.(err.message)
        }
      } else {
        onError?.('Failed to start recording')
      }
    }
  }, [stopMediaTracks, startLevelMonitoring, transcribe, onError])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }, [])

  return { state, levelHistory, startRecording, stopRecording }
}
